import ByteGuide, { IStep } from 'byte-guide';
import { useOrgValue } from 'hooks/business/system/useOrg';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import usePermission from 'hooks/business/usePermission';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import useLayerLock from 'hooks/useLayerLock';
import useModal from 'hooks/useModal';
import { isChatppProduct } from 'productLine';
import { useEffect, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { guideCls, SkipBtn } from 'views/Assistant/Group';

function Guide() {
  const { t } = useTranslate();

  const lang = useSystemLanguageValue();

  const { SHOP_MANAGER } = usePermission();

  const { AIDE: aideTrigger = false } = useTriggerMapValue();

  const createTrigger = useCreateTrigger();

  const { gained, release, acquire } = useLayerLock('navGuide', 50, true);

  const { visible, show, hide, update, meta: guideStep = 0 } = useModal<number>(false, 0);

  const orgValue = useOrgValue();
  const isExpired = orgValue?.isExpired;

  const guideSteps = useMemo(() => {
    return [
      {
        selector: 'div [data-guide="nav-guide-step-1"]',
        title: t('guide.支持更多操作'),
        content: t('guide.支持切换状态、修改账号信息、系统设置、退出登录。'),
        placement: 'right-top',
      },
      ...(SHOP_MANAGER
        ? [
            {
              selector: 'div [data-guide="nav-guide-step-2"]',
              title: t('guide.跟单助手重大升级，陪伴履约全流程'),
              content: t(
                'guide.可进行接待设置、催单催付自动化、实时挽回、追踪评价等。根据引导轻松配置跟单场景任务，让整个接待转化全程无忧！',
              ),
              placement: 'right',
            },
            {
              selector: 'div [data-guide="nav-guide-step-3"]',
              title: t('guide.让管理更便捷高效'),
              content: t('guide.可进行店铺管理、帐号管理、买家黑名单等功能管理。'),
              placement: 'right',
            },
          ]
        : [
            {
              selector: 'div [data-guide="nav-guide-step-2"]',
              title: '快捷回复设置移动到此处啦～',
              content: '原设置中快捷回复功能，移动至跟单助手中。',
              placement: 'right',
            },
          ]),
      {
        selector: 'div [data-guide="nav-guide-step-4"]',
        title: t('guide.系统偏好设置'),
        content: isChatppProduct ? (
          <>
            {t('guide.新增极致蓝、活力橘主题色')}
            <br />
            {t('guide.可进行主题色切换、中差评提醒、消息声音等设置。')}
          </>
        ) : (
          t('guide.可进行中差评提醒、消息声音等设置。')
        ),
        placement: 'right-bottom',
      },
    ] as IStep[];
  }, [SHOP_MANAGER, t]);

  useEffect(() => {
    if (!isExpired) {
      setTimeout(() => {
        show();
      }, 2000); // 不延迟的话， guide 位置异常，原因未知
    }
  }, [isExpired, show]);

  const actualVisible = visible && !aideTrigger;

  const finallyVisible = gained && actualVisible;

  useEffect(() => {
    if (!actualVisible) {
      release();

      return () => {};
    }

    acquire();

    return () => {
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualVisible]);

  return (
    <ByteGuide
      steps={guideSteps}
      visible={finallyVisible}
      lang={lang === 'zh-CN' ? 'zh' : 'en'}
      mask={false}
      onClose={() => {
        release();
        hide();

        if (guideStep === guideSteps.length - 1) {
          createTrigger({
            trigger: 'AIDE',
          });
        }
      }}
      afterStepChange={(step) => update(visible, step)}
      closeEle={
        guideStep === guideSteps.length - 1 ? (
          <></>
        ) : (
          <SkipBtn type="text">{t('guide.下次提醒')}</SkipBtn>
        )
      }
      modalClassName={guideCls}
      stepText={(stepIndex, stepCount) => {
        return `${stepIndex}/${stepCount}`;
      }}
      nextText={t('authShop.下一步')}
    />
  );
}

export default Guide;
